/**
 * claude-rules.ts — The managed "SmartStack Git Safety" block that
 * `ss install` / `ss upgrade` merges into `~/.claude/CLAUDE.md`.
 *
 * The block is delimited by the BEGIN/END markers below so the merge is
 * idempotent and reversible: re-running install replaces the block in place,
 * `ss uninstall` strips it, and anything the user wrote OUTSIDE the markers is
 * never touched. Edit the rule text HERE (versioned), never in ~/.claude.
 *
 * Rationale: the git-guard PreToolUse hook is the HARD enforcement (it blocks
 * the command); this block is the SOFT layer — it tells Claude the rule up
 * front so it proposes instead of attempting. Defense in depth.
 *
 * The rule is TWO-LANE, not a blanket ban: 🟢 committing through `/gitflow commit`
 * and running `/efcore create|apply` on a work branch are autonomous, because the
 * sanctioned CLIs already grade the risk (efcore/cli/lib/migration-policy.ts —
 * 🔴 blocked, 🟡 needs `confirm`, 🟢 passes). 🔴 (push, tag, PR, merge, history
 * rewrite, protected branches, non-local DB) still needs an explicit go-ahead in
 * the current turn. A blanket "never" made Claude beg for every checkpoint and
 * broke the dev flow; the CLIs, not the prose, are what enforce the boundary.
 */

export const CLAUDE_MD_MARKER_BEGIN =
  '<!-- BEGIN SmartStack Git Safety (managed by `ss install` — edit templates source, not here) -->';
export const CLAUDE_MD_MARKER_END = '<!-- END SmartStack Git Safety -->';

/** The rule text placed between the markers (the user's non-negotiable Git/EF rules). */
export const GIT_SAFETY_RULES = `## Règles Git & migrations — deux voies

Le risque est GRADUÉ, pas binaire. Deux voies : 🟢 tu agis seul, 🔴 tu me demandes.
Le doute n'est pas une voie : toute situation non listée en 🟢 est 🔴.

### 🟢 Voie verte — autonome, sans demander

Sur une branche de travail (\`feature/*\`, \`develop\`) :

- **Commiter via le chemin sanctionné** : \`/gitflow commit\` (CLI \`skills/gitflow/cli/commit/\`).
  Tu commites au fil du travail, sans annoncer ni attendre. Un commit local est réversible —
  ce n'est pas un événement.
- **Migrations EF Core via \`/efcore\`** : \`create\` et \`apply\`, tant que le CLI rend un verdict 🟢
  (politique 3 paliers, \`efcore/cli/lib/migration-policy.ts\`). Concrètement : migration additive
  sur \`feature/*\`/\`develop\`, et application sur une base **prouvée locale** : loopback
  (\`localhost\`, \`127.0.0.1\`, \`(localdb)\`, \`.\`), le hostname de la machine (instance nommée
  type \`MONPC\\SQLEXPRESS\`), ou un hôte déclaré dans \`.gitflow/config.json\` →
  \`efcore.localHosts\`. Cette déclaration est une décision UTILISATEUR, faite une fois —
  tu ne l'écris JAMAIS toi-même. Azure SQL (\`*.database.windows.net\`) n'est jamais local.

Le verdict du CLI fait autorité : s'il répond 🟢, tu ne me redemandes pas.

### 🔴 Voie rouge — validation explicite dans le tour courant

Tu PROPOSES en une phrase et tu ATTENDS mon « oui ». Tu n'anticipes jamais ma réponse.

- \`git push\`, \`git tag\`, ouverture de PR, merge, tout artefact distant (GitHub/GitLab)
- création/suppression de branche, worktree, \`reset --hard\`, rebase, toute réécriture
  d'historique, toute ref hors \`refs/heads|remotes|tags\`, tout dépôt/checkpoint parallèle
- TOUTE opération sur \`main\`, \`release/*\`, \`hotfix/*\` — y compris un simple commit
- migration dont le CLI rend 🟡 ou 🔴 : \`Up()\` destructif (Drop/narrowing), \`migrations remove\`,
  \`database update\` sur base distante ou non prouvée locale, squash, revert
- \`npm publish\` — la publication passe par le pipeline Azure, jamais à la main

Un 🟡 n'est pas un refus : tu me montres le diff et tu attends mon \`confirm\`.

### Squash de migrations

Un squash ne supprime JAMAIS une migration déjà présente dans la branche de référence
(la cible du merge : feature→develop ; develop, release, hotfix→main). On ne consolide
que ce qui est propre à la branche.

### Contournement interdit

Les hooks \`PreToolUse\` \`git-guard\` / \`ef-guard\` bloquent le **shell brut** (\`git commit\`,
\`dotnet ef migrations add\` lancés en Bash). C'est voulu : la voie verte passe par les CLIs
\`/gitflow\` et \`/efcore\`, qui appliquent les gardes. Tu ne contournes jamais un guard, tu
n'exportes jamais \`GIT_GUARD_OFF\` / \`EF_GUARD_OFF\` (kill-switch UTILISATEUR uniquement),
tu n'appelles jamais \`git\` / \`dotnet ef\` en brut pour « aller plus vite ».

### En cas de doute

Effet de bord non listé en 🟢 sur l'arbre git, le dépôt distant, la base de données ou le
système de fichiers versionné → tu t'arrêtes et tu demandes. Le silence n'est pas un oui.`;

/** The full managed block (markers + rules), with a trailing newline. */
export function buildClaudeMdBlock(): string {
  return `${CLAUDE_MD_MARKER_BEGIN}\n\n${GIT_SAFETY_RULES}\n\n${CLAUDE_MD_MARKER_END}`;
}

// ============================================================================
// SmartStack CLI Support — second managed block
// ============================================================================
//
// The client-site incident this closes: a colocated skill CLI crashes and
// Claude tries to FIX THE DEPLOYED COPY under ~/.claude — dead on arrival
// (no sources at the client, overwritten by the next `ss update`) and a
// masked bug report. Same defense-in-depth pair as Git Safety: the
// skills-guard PreToolUse hook is the HARD layer (blocks the write), this
// block is the SOFT layer — the rule is in context up front, so Claude routes
// to /support-report instead of attempting the edit.

export const SUPPORT_MD_MARKER_BEGIN =
  '<!-- BEGIN SmartStack CLI Support (managed by `ss install` — edit templates source, not here) -->';
export const SUPPORT_MD_MARKER_END = '<!-- END SmartStack CLI Support -->';

/** The rule text placed between the Support markers. */
export const CLI_SUPPORT_RULES = `## Erreurs de la CLI SmartStack — tu n'es pas le mainteneur

Les fichiers installés sous \`~/.claude/\` (\`skills/\`, \`agents/\`, \`hooks/\`, \`commands/\`)
sont des COPIES DÉPLOYÉES par la CLI SmartStack : les sources ne sont pas sur ce poste et
toute modification locale sera écrasée à la prochaine mise à jour. Tu ne les édites JAMAIS —
le hook \`skills-guard\` bloque l'écriture, et il a raison. \`SKILLS_GUARD_OFF\` est un
kill-switch UTILISATEUR uniquement : tu ne l'exportes jamais toi-même.

### Quand un CLI colocalisé plante (stack trace, stdout non-JSON, exit inexpliqué)

La voie sanctionnée est \`/support-report\` — jamais un « fix » local, jamais du code écrit
à la main à la place d'un scaffolder :

1. **Vérifie que l'erreur est réelle.** Une envelope \`success:false\` avec \`errors[]\` est
   un REFUS CONTRÔLÉ : ton spec est en cause, corrige-le. Sinon, reproduis la commande à
   l'identique et capture les sorties — une hypothèse non reproduite ne se rapporte pas.
2. **Contrôle les versions.** Le CLI de rapport compare la CLI installée à npm : si une
   version plus récente existe, tu PROPOSES la mise à jour
   (\`npm i -g @atlashub/smartstack-cli@latest\` puis \`ss update\`), tu attends le « oui »,
   puis tu retestes — le défaut est peut-être déjà corrigé.
3. **Produis le rapport.** \`skills/support-report/cli/create\` écrit le dossier
   \`.smartstack/support/<empreinte>/\` (versions, commande verbatim, captures, secrets
   masqués) — à transmettre à **support@atlashub.ch** par l'utilisateur.
4. **Continue.** L'artefact en échec est SKIPPÉ (doctrine \`cli.runtime-error\`) ; le
   pipeline ne s'arrête pas pour un rapport déposé.`;

/** The full managed Support block (markers + rules). */
export function buildSupportMdBlock(): string {
  return `${SUPPORT_MD_MARKER_BEGIN}\n\n${CLI_SUPPORT_RULES}\n\n${SUPPORT_MD_MARKER_END}`;
}
